import { Component, Output, EventEmitter } from '@angular/core';
import { LookUpsService } from 'src/shared/lookups.service';
import {
	FormConfig,
	CustomFormAccessor,
} from 'src/shared/form-models/form-cva.component';
import { Validators, NgControl, FormBuilder } from '@angular/forms';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import {
	buildArrayFromEnum,
	filterStates,
	idComparer,
} from 'src/shared/utilities/utility-functions';
import {
	FieldWorkerPerDiemAgreementType,
	FieldWorkerPayrollType,
	PersonnelClient,
	FieldWorkerType,
	FieldWorkerLanguage,
} from 'src/shared/api';
import { takeUntil } from 'rxjs/operators';
import {
	displayPayrollType,
	displayDescription,
	displayFieldTeam,
	displayLanguage
} from 'src/shared/enums';
import {
	displayAgreementType,
	actualFieldWorkerTypeIds,
} from '../personnel-detail.utility';
import { PersonnelDetailGeneralInformation } from '../personnel-detail.models';
import { AuthService } from 'src/admin-app/shared/auth/auth.service';
import { TerminateFieldWorkerDialogComponent } from '../terminate-field-worker-dialog/terminate-field-worker-dialog.component';
import { MatDialog } from '@angular/material/dialog';

const personnelGeneralInformationFormConfig: FormConfig<PersonnelDetailGeneralInformation> =
	{
		id: [''],
		firstName: ['', Validators.required],
		lastName: ['', Validators.required],
		jobTitle: [''],
		employeeCode: [''],
		paycomId: ['', Validators.required],
		regularHourlyRate: [
			null,
			[
				Validators.required,
				Validators.min(0),
				CustomValidators.mustBeNumeric,
			],
		],
		payrollType: ['', Validators.required],
		type: ['', Validators.required],
		status: [''],
		team: ['', Validators.required],
		perDiemAgreementType: [''],
		statedLivedIn: [
			'',
			[Validators.required, CustomValidators.isValidState],
		],
		cityLivedIn: [''],
		utilityTravelOverrideRate: [''],
		isExcludedFromPayroll: [''],
		mealBreakAttestationSigned: [''],
		language: ['', Validators.required]
	};

@Component({
	selector: 'rpfsa-personnel-general-info',
	templateUrl: './general-info.component.html',
})
export class PersonnelGeneralInformationComponent extends CustomFormAccessor<PersonnelDetailGeneralInformation> {
	@Output() selectPayrollType = new EventEmitter<FieldWorkerPayrollType>();
	@Output() fieldWorkerTypeSelected = new EventEmitter<number>();

	readonly perDiemAgreementTypes = buildArrayFromEnum(
		FieldWorkerPerDiemAgreementType
	);
	readonly languages = buildArrayFromEnum(FieldWorkerLanguage);
	readonly payrollTypes = buildArrayFromEnum(FieldWorkerPayrollType);
	readonly filteredStatesLivedIn$ = filterStates(
		this.form.get('statedLivedIn')
	);
	readonly fieldWorkerTypes$ = this.lookupsService.fieldWorkerTypes;
	readonly employeeStatuses$ = this.lookupsService.employeeStatuses;
	readonly fieldTeams$ = this.lookupsService.fieldTeams;

	readonly idComparer = idComparer;
	readonly displayPayrollType = displayPayrollType;
	readonly displayDescription = displayDescription;
	readonly displayFieldTeam = displayFieldTeam;
	readonly displayLanguage = displayLanguage;
	readonly displayAgreementType = displayAgreementType;
	readonly canViewHourlyRate =
		this.authService.isUserRolePayroll || this.authService.isUserRoleHR;

	worksInTheField = true;

	constructor(
		private readonly lookupsService: LookUpsService,
		private readonly authService: AuthService,
		private readonly personnelClient: PersonnelClient,
		public ngControl: NgControl,
		public fb: FormBuilder,
		public terminateFieldWorkerDialog: MatDialog
	) {
		super(ngControl, fb, personnelGeneralInformationFormConfig);
	}

	private previousStatus: any;
	// model --> view
	writeValue(value: PersonnelDetailGeneralInformation): void {
		if (value) {
			this.form.get('employeeCode').asyncValidator =
				CustomValidators.isEmployeeCodeUnique(
					this.personnelClient,
					value.id
				);
			this.form.get('paycomId').asyncValidator =
				CustomValidators.isPaycomIdUnique(
					this.personnelClient,
					value.id
				);
			if (
				value.type &&
				!actualFieldWorkerTypeIds.includes(value.type.id)
			) {
				this.disableFieldWorkerSpecificFields();
			}
		}
		super.writeValue(value);
		this.previousStatus = this.form.get('status').value;
	}

	// view --> model
	registerOnChange(fn: (value: PersonnelDetailGeneralInformation) => void) {
		this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	selectFieldWorkerType(type: FieldWorkerType): void {
		if (actualFieldWorkerTypeIds.includes(type.id)) {
			this.enableFieldWorkerSpecificFields();
		} else {
			this.disableFieldWorkerSpecificFields();
		}
		this.fieldWorkerTypeSelected.next(type.id);
	}

	openTerminateDialog(): void {
		const dialogRef = this.terminateFieldWorkerDialog.open(
			TerminateFieldWorkerDialogComponent,
			{
				width: '400px',
			}
		);

		dialogRef.afterClosed().subscribe((accepted) => {
			if (!accepted) {
				this.form
					.get('status')
					?.setValue(this.previousStatus, { emitEvent: false });
			}
		});
	}

	ngOnInit() {
		this.form.get('status')?.valueChanges.subscribe((newValue) => {
			if (newValue?.description === 'Terminated') {
				this.openTerminateDialog();
			}
		});
	}

	private disableFieldWorkerSpecificFields(): void {
		this.form.get('statedLivedIn').reset('');
		this.form.get('statedLivedIn').clearValidators();
		this.form.get('statedLivedIn').updateValueAndValidity();
		this.form.get('team').reset();
		this.form.get('team').clearValidators();
		this.form.get('team').updateValueAndValidity();
		this.worksInTheField = false;
	}

	private enableFieldWorkerSpecificFields(): void {
		this.form
			.get('statedLivedIn')
			.setValidators([
				Validators.required,
				CustomValidators.isValidState,
			]);
		this.form.get('statedLivedIn').updateValueAndValidity();
		this.form.get('team').setValidators([Validators.required]);
		this.form.get('team').updateValueAndValidity();
		this.worksInTheField = true;
	}
}
